/**
 * Photos des plats de la carte (« Ma Carte ») : MenuItem.image, URL de
 * téléchargement Storage d'un fichier `restaurants/{restaurantId}/menu/{fichier}.jpg`.
 * Le chemin du fichier n'est PAS stocké : il est déduit de l'URL validée
 * (menuPhotoPath), il n'y a donc rien à falsifier ni à contrôler dans les règles.
 *
 * Fonctions pures : elles calculent la nouvelle carte à partir de celle lue dans
 * une transaction Firestore (firestoreService.ts) et ne touchent qu'au plat
 * concerné. Règle commune : la photo d'un plat vient TOUJOURS du document
 * enregistré (jamais de l'état local de l'éditeur), seules les fonctions photo
 * la modifient. Un enregistrement de la carte ne peut donc pas effacer une photo
 * envoyée entre-temps, et inversement.
 *
 * Miroir côté règles : menuPhotosOk() et storageUrlRe() dans firestore.rules,
 * dossier menu/ dans storage.rules. Copie pour l'affichage mobile :
 * apps/mobile/src/utils/storageImageUrl.ts. Testé sur émulateurs par
 * tests/rules/carte.rules.test.mjs.
 *
 * Syntaxe TypeScript « effaçable » uniquement (pas d'enum, imports de types
 * seulement) : le fichier est importé tel quel par les tests Node.
 */
import type { MenuItem } from './types';

/** Plats ajoutés ou modifiés au plus par écriture (miroir de menuPhotosOk dans firestore.rules). */
export const MAX_MENU_CHANGES_PER_WRITE = 20;

/** Côté maximal d'une photo de plat : elle n'est affichée qu'en vignette (64-96 px web, 72 pt mobile). */
export const MENU_PHOTO_MAX_DIMENSION = 480;

/** Message des opérations refusées sur un plat sans identifiant. */
export const MENU_ITEM_ID_MISSING = "Ce plat n'a pas d'identifiant : utilisez « Réparer la carte » avant de le modifier.";

export interface MenuPhoto {
    image: string;
}

const escapeRe = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * URL de téléchargement d'un fichier menu/ du restaurant — miroir de
 * storageUrlRe('menu') dans firestore.rules : buckets prod / preprod, nom de
 * fichier capturé, suffixe de requête limité (alt=media&token=<uuid>).
 */
const menuPhotoUrlRe = (restaurantId: string | number) => new RegExp(
    '^https://firebasestorage\\.googleapis\\.com/v0/b/(?:atable-114d4|atable-preprod)\\.firebasestorage\\.app'
    + `/o/restaurants%2F${escapeRe(String(restaurantId))}%2Fmenu%2F([A-Za-z0-9_-]{1,64})\\.jpg\\?[A-Za-z0-9=&%._~-]{0,500}$`,
);

/** URL de téléchargement Storage d'un fichier menu/ de ce restaurant (miroir des règles). */
export function isMenuPhotoUrl(restaurantId: string | number, url: unknown): url is string {
    return typeof url === 'string' && menuPhotoUrlRe(restaurantId).test(url);
}

/** Photo du plat sûre à afficher, ou null (absente, externe, autre restaurant…). */
export function menuPhotoUrl(restaurantId: string | number, item: Partial<MenuItem> | null | undefined): string | null {
    return item && isMenuPhotoUrl(restaurantId, item.image) ? item.image : null;
}

/** Chemin Storage de la photo du plat, déduit de son URL validée, ou null. */
export function menuPhotoPath(restaurantId: string | number, item: Partial<MenuItem> | null | undefined): string | null {
    const match = item && typeof item.image === 'string' ? menuPhotoUrlRe(restaurantId).exec(item.image) : null;
    return match ? `restaurants/${restaurantId}/menu/${match[1]}.jpg` : null;
}

/** Photo valide du plat enregistré, ou null. */
export function menuPhotoOf(restaurantId: string | number, item: Partial<MenuItem> | null | undefined): MenuPhoto | null {
    const image = menuPhotoUrl(restaurantId, item);
    return image ? { image } : null;
}

/** Identifiant de plat utilisable : chaîne non vide. */
export function isMenuItemId(id: unknown): id is string {
    return typeof id === 'string' && id.trim() !== '';
}

const requireItemId = (id: unknown) => {
    if (!isMenuItemId(id)) throw new Error(MENU_ITEM_ID_MISSING);
};

/**
 * Identifiant aléatoire d'un nouveau plat, choisi à l'ouverture de la fenêtre :
 * un enregistrement rejoué (réseau coupé) ne crée pas de doublon.
 * crypto.randomUUID n'existe pas en http sur le réseau local, d'où getRandomValues.
 */
export function newMenuItemId(): string {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    return `item-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Indices des plats qui ne peuvent pas être modifiés sans risque : entrée qui
 * n'est pas un objet, identifiant absent ou vide, identifiant déjà porté par un
 * plat précédent (les opérations ciblent un plat par son identifiant).
 */
export function menuItemsNeedingId(menu: readonly unknown[]): number[] {
    const seen = new Set<string>();
    const indexes: number[] = [];
    menu.forEach((item, index) => {
        const id = item && typeof item === 'object' ? (item as MenuItem).id : undefined;
        if (!isMenuItemId(id) || seen.has(id)) indexes.push(index);
        else seen.add(id);
    });
    return indexes;
}

/** Le plat sans photo ni valeurs undefined (refusées par Firestore). */
export function withoutPhoto(item: MenuItem): MenuItem {
    // imageStoragePath : champ d'une version de développement, plus écrit.
    const { image: _image, imageStoragePath: _path, ...rest } = item as MenuItem & { imageStoragePath?: unknown };
    return Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== undefined)) as unknown as MenuItem;
}

/** Plat dont la photo est remplacée par `photo` (ou retirée si null). */
const withPhoto = (item: MenuItem, photo: MenuPhoto | null): MenuItem =>
    (photo ? { ...withoutPhoto(item), ...photo } : withoutPhoto(item));

export function menuOf(data: { menu?: unknown } | undefined): MenuItem[] {
    return Array.isArray(data?.menu) ? (data.menu as MenuItem[]) : [];
}

/** Représentation stable (clés triées) pour comparer deux plats comme le font les règles. */
function canonical(value: unknown): string {
    if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
    if (value && typeof value === 'object') {
        return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${canonical((value as any)[k])}`).join(',')}}`;
    }
    return JSON.stringify(value) ?? 'undefined';
}

const sameItem = (a: unknown, b: unknown) => canonical(a) === canonical(b);

/**
 * Plats de `next` qui n'existent pas à l'identique dans `previous` : ceux que
 * les règles contrôlent (miroir de menu.removeAll(ancienne carte)).
 */
export function changedMenuItems(next: readonly unknown[], previous: readonly unknown[]): unknown[] {
    const before = new Set(previous.map(canonical));
    return next.filter((item) => !before.has(canonical(item)));
}

/**
 * Plat enregistré + champs (hors photo) saisis dans l'éditeur. Si rien n'a
 * changé, le plat enregistré est gardé tel quel (même avec une ancienne image
 * invalide : il n'est alors ni modifié, ni compté comme modifié).
 */
function mergeEdits(restaurantId: string | number, stored: MenuItem, edited: MenuItem): MenuItem {
    const merged = { ...withoutPhoto(stored), ...withoutPhoto(edited) };
    if (sameItem(merged, withoutPhoto(stored))) return stored;
    return withPhoto(merged, menuPhotoOf(restaurantId, stored));
}

/**
 * Ajout (identifiant inconnu) ou modification d'un plat, hors photo : seul ce
 * plat change, il garde sa photo enregistrée (et les champs qu'il porterait en plus).
 */
export function upsertMenuItem(restaurantId: string | number, current: readonly MenuItem[], item: MenuItem): MenuItem[] {
    requireItemId(item.id);
    const index = current.findIndex((m) => m.id === item.id);
    if (index === -1) return [...current, withoutPhoto(item)];
    const next = [...current];
    next[index] = mergeEdits(restaurantId, current[index], item);
    return next;
}

/**
 * Suppression d'un plat : le premier plat portant cet identifiant, celui que
 * ciblent toutes les opérations (un doublon éventuel, signalé à réparer, reste).
 * `removed` : le plat supprimé, dont la photo est à supprimer de Storage si
 * plus aucun plat ne l'utilise (unusedMenuPhotoPaths).
 */
export function removeMenuItem(current: readonly MenuItem[], itemId: string): { next: MenuItem[]; removed?: MenuItem } {
    requireItemId(itemId);
    const index = current.findIndex((m) => m.id === itemId);
    if (index === -1) return { next: [...current] };
    return { next: current.filter((_, i) => i !== index), removed: current[index] };
}

/**
 * Chemins Storage des photos de `candidates` qu'aucun plat de `remaining`
 * n'utilise plus : seuls ceux-là peuvent être supprimés sans casser une image.
 */
export function unusedMenuPhotoPaths(
    restaurantId: string | number,
    candidates: readonly (MenuItem | null | undefined)[],
    remaining: readonly MenuItem[],
): string[] {
    const used = new Set(remaining.map((m) => menuPhotoPath(restaurantId, m)).filter(Boolean));
    const paths = candidates.map((m) => menuPhotoPath(restaurantId, m)).filter((p): p is string => !!p && !used.has(p));
    return [...new Set(paths)];
}

/**
 * Pose (ou retire, photo = null) la photo d'un plat ; les autres plats sont
 * inchangés. null si le plat n'existe plus (supprimé entre-temps).
 */
export function setMenuItemPhoto(
    current: readonly MenuItem[],
    itemId: string,
    photo: MenuPhoto | null,
): { next: MenuItem[]; previous: MenuItem } | null {
    requireItemId(itemId);
    const index = current.findIndex((m) => m.id === itemId);
    if (index === -1) return null;
    const next = [...current];
    next[index] = withPhoto(current[index], photo);
    return { next, previous: current[index] };
}

/**
 * Publication de la carte de l'éditeur, NON destructive : seuls les textes des
 * plats présents des deux côtés sont repris (dans l'ordre de l'éditeur) ; un
 * plat ajouté ailleurs est conservé (à la fin), un plat supprimé ailleurs n'est
 * pas recréé, aucune photo n'est supprimée. Un plat que l'éditeur n'a pas
 * changé reste identique au plat enregistré.
 */
export function mergeMenuForPublish(restaurantId: string | number, current: readonly MenuItem[], local: readonly MenuItem[]): MenuItem[] {
    // Premier plat enregistré de chaque identifiant (celui que ciblent les opérations).
    const stored = new Map<string, MenuItem>();
    for (const m of current) if (isMenuItemId(m?.id) && !stored.has(m.id)) stored.set(m.id, m);
    const consumed = new Set<MenuItem>();
    const next: MenuItem[] = [];
    for (const item of local) {
        const before = isMenuItemId(item?.id) ? stored.get(item.id) : undefined;
        if (!before || consumed.has(before)) continue;
        consumed.add(before);
        next.push(mergeEdits(restaurantId, before, item));
    }
    for (const item of current) if (!consumed.has(item)) next.push(item);
    return next;
}

/**
 * Réparation : attribue un identifiant à au plus `max` plats qui n'en ont pas
 * (ou en ont un en double), en retirant une photo invalide (le plat devient
 * « modifié », donc contrôlé par les règles). Les entrées qui ne sont pas des
 * objets sont retirées (ni affichables ni modifiables).
 */
export function repairMenuItemIds(
    restaurantId: string | number,
    current: readonly MenuItem[],
    max = MAX_MENU_CHANGES_PER_WRITE,
): { next: MenuItem[]; repaired: number; remaining: number } {
    const indexes = menuItemsNeedingId(current);
    const toRepair = new Set(indexes.slice(0, max));
    const next: MenuItem[] = [];
    current.forEach((item, index) => {
        if (!toRepair.has(index)) next.push(item);
        else if (item && typeof item === 'object') {
            next.push(withPhoto({ ...item, id: newMenuItemId() }, menuPhotoOf(restaurantId, item)));
        }
    });
    return { next, repaired: toRepair.size, remaining: indexes.length - toRepair.size };
}

/**
 * Reporte dans la carte locale (éditeur) le seul plat `itemId` tel qu'il est
 * enregistré dans `saved` (ou le retire s'il n'y est plus) : une réponse
 * arrivée en retard ne peut pas écraser le reste de la carte locale.
 */
export function mergeSavedItem(local: readonly MenuItem[], saved: readonly MenuItem[], itemId: string): MenuItem[] {
    const fresh = saved.find((m) => m.id === itemId);
    const index = local.findIndex((m) => m.id === itemId);
    if (!fresh) return index === -1 ? [...local] : local.filter((m) => m.id !== itemId);
    if (index === -1) return [...local, fresh];
    const next = [...local];
    next[index] = fresh;
    return next;
}
